import qs from 'qs';
import { StoredRequest } from '@core/utils/store/stored-request-decorator';
import {
  DatesAtAGlanceReportResponse,
  NewClosedProjectsReportResponse,
  OverdueTasksReportResponse,
  ProjectDurationReportResponse,
  ReportsFilterOptionsResponse,
  ReportsFilters,
  StructuralPassFailReportResponse,
} from '@models/interfaces';
import { ReportsActions } from '@store/actions';
import { CommonHttpService } from './common-http.service';

type ReportsRequestOptions = { silent?: boolean };

const getReportsParams = (filters: ReportsFilters): ReportsFilters =>
  Object.entries(filters).reduce(
    (acc: ReportsFilters, [key, value]: [string, unknown]) => {
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && !value.length)
      ) {
        return acc;
      }

      return {
        ...acc,
        [key]: value,
      };
    },
    {},
  );

const getReportsUrl = (
  apiUrl: string,
  path: string,
  params: ReportsFilters,
): string => {
  const queryString = qs.stringify(params, { arrayFormat: 'repeat' });

  return `${apiUrl}${path}${queryString ? `?${queryString}` : ''}`;
};

const getStructuralPassFailParams = (
  filters: ReportsFilters,
): ReportsFilters => {
  const params = getReportsParams(filters);

  delete params.paceTask;
  delete params.paceTaskScreen;

  return params;
};

const getProjectDurationParams = (filters: ReportsFilters): ReportsFilters => {
  const params = getReportsParams(filters);

  delete params.paceTask;
  delete params.paceTaskScreen;

  return params;
};

const getNewClosedProjectsParams = (
  filters: ReportsFilters,
): ReportsFilters => {
  const params = getReportsParams(filters);

  delete params.paceTask;
  delete params.paceTaskScreen;

  return params;
};

export class ReportsHttpService extends CommonHttpService {
  @StoredRequest(ReportsActions.getDatesAtAGlance)
  getDatesAtAGlance(
    filters: ReportsFilters,
    _options?: ReportsRequestOptions,
  ): Promise<DatesAtAGlanceReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/dates-at-a-glance',
        getReportsParams(filters),
      ),
      null,
    );
  }

  @StoredRequest(ReportsActions.getFilterOptions)
  getFilterOptions(
    _options?: ReportsRequestOptions,
  ): Promise<ReportsFilterOptionsResponse> {
    return this.http.callApi.get(`${this.apiUrl}/reports/filter-options`);
  }

  @StoredRequest(ReportsActions.getNewClosedProjects)
  getNewClosedProjects(
    filters: ReportsFilters,
    _options?: ReportsRequestOptions,
  ): Promise<NewClosedProjectsReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/new-closed-projects',
        getNewClosedProjectsParams(filters),
      ),
      null,
    );
  }

  @StoredRequest(ReportsActions.getOverdueTasks)
  getOverdueTasks(
    filters: ReportsFilters,
    _options?: ReportsRequestOptions,
  ): Promise<OverdueTasksReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/overdue-tasks',
        getReportsParams(filters),
      ),
      null,
    );
  }

  @StoredRequest(ReportsActions.getProjectDuration)
  getProjectDuration(
    filters: ReportsFilters,
    _options?: ReportsRequestOptions,
  ): Promise<ProjectDurationReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/project-duration',
        getProjectDurationParams(filters),
      ),
      null,
    );
  }

  @StoredRequest(ReportsActions.getStructuralPassFail)
  getStructuralPassFail(
    filters: ReportsFilters,
    _options?: ReportsRequestOptions,
  ): Promise<StructuralPassFailReportResponse> {
    return this.http.callApi.get(
      getReportsUrl(
        this.apiUrl,
        '/reports/structural-pass-fail',
        getStructuralPassFailParams(filters),
      ),
      null,
    );
  }
}
